import { EventPendingReview } from 'src/apis/eventCandidateAPI'

// The two review notes about the link that are shown under the Pending tag.
export type LinkNote = '' | 'source_unverified' | 'source_is_host_page'

// What a row of the events list tells about an event that waits for review.
export interface PendingReviewInfo {
	candidateId: string
	hostName: string
	// Empty unless the link is a plain http or https one.
	sourceUrl: string
	hostPageUrl: string
	notes: string[]
	// The one review note code that is shown in the Review column, or empty.
	linkNote: LinkNote
}

/** True only for http and https links, so nothing else is ever opened or shown. */
export const isHttpUrl = (value?: string | null) => {
	if (!value || typeof value !== 'string') return false
	try {
		const url = new URL(value.trim())
		return url.protocol === 'http:' || url.protocol === 'https:'
	} catch {
		return false
	}
}

const REVIEW_NOTE_TEXT: Record<string, string> = {
	end_time_assumed: 'End time was not on the page; 2 hours was assumed',
	price_unknown: 'Price was not stated; the event shows no price',
	currency_missing: 'A price was found with no currency; the event shows no price',
	category_missing: 'No category matched; the event has none',
	host_link_missing: 'No host link was found',
	cover_image_missing: 'No cover image was found',
	source_unverified: 'Link not checked: the site needs a login, open it yourself',
	source_is_host_page: "Link is the organizer's page, not the event's own page",
}

/**
 * What the finder assumed or could not find, in plain words.
 * A code with no wording of its own is shown as it came.
 */
export const getReviewNotes = (codes: unknown): string[] => {
	const notes = (Array.isArray(codes) ? codes : [])
		.filter((code) => typeof code === 'string' && code.trim() !== '')
		.map((code: string) => REVIEW_NOTE_TEXT[code.trim()] || code.trim())
	return Array.from(new Set<string>(notes))
}

/** The link note to show under the Pending tag: "not checked" wins when both codes are there. */
const getLinkNote = (codes: unknown): LinkNote => {
	const list = Array.isArray(codes) ? codes.map((code) => (typeof code === 'string' ? code.trim() : '')) : []
	if (list.includes('source_unverified')) return 'source_unverified'
	if (list.includes('source_is_host_page')) return 'source_is_host_page'
	return ''
}

const LINK_NOTE_SHORT_TEXT = {
	source_unverified: 'Link not checked',
	source_is_host_page: "Organizer's page",
}

/** The short line for the Review column and the full sentence for its hover title; null for no note. */
export const getLinkNoteText = (linkNote: LinkNote) =>
	linkNote ? { short: LINK_NOTE_SHORT_TEXT[linkNote], full: REVIEW_NOTE_TEXT[linkNote] } : null

const text = (value: unknown) => (typeof value === 'string' ? value.trim() : '')

/** The review details of an event row, or null for a normal event. */
export const getPendingReview = (event: any): PendingReviewInfo | null => {
	const review: Partial<EventPendingReview> | null | undefined =
		event?.pending_review
	if (!review || typeof review !== 'object' || Array.isArray(review)) {
		return null
	}
	const sourceUrl = text(review.source_url)
	const hostPageUrl = text(review.host_contact_url)
	return {
		candidateId: text(review.candidate_id),
		hostName: text(review.host_name),
		sourceUrl: isHttpUrl(sourceUrl) ? sourceUrl : '',
		hostPageUrl: isHttpUrl(hostPageUrl) ? hostPageUrl : '',
		notes: getReviewNotes(review.review_notes),
		linkNote: getLinkNote(review.review_notes),
	}
}

export const isPendingReview = (event: any) => getPendingReview(event) !== null

/** The page to open for a pending event: its source link, else the organizer's page, else empty. */
export const getReviewLink = (review: PendingReviewInfo | null) =>
	review ? review.sourceUrl || review.hostPageUrl : ''

/**
 * The link that opens an event in the app: the share link every event has. On a phone or an
 * iPad it opens the app; signed in there as the account that owns found events, staff see a
 * pending event as users will. Empty when the row has none or it is not an http(s) link.
 */
export const getAppLink = (event: any) => {
	const link = text(event?.share_link)
	return isHttpUrl(link) ? link : ''
}

/** How many events wait for review, or null while the statistics do not say. */
export const getPendingCount = (statistics: any): number | null => {
	const value = statistics?.pending_review
	if (value === null || value === undefined || value === '') return null
	const count = Number(value)
	return Number.isInteger(count) && count >= 0 ? count : null
}

export const getPendingFilterLabel = (count: number | null) =>
	count === null ? 'Pending review' : `Pending review (${count})`
